import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { MessageSquare, Send, ChevronLeft, ChevronRight, Crown, AlertCircle } from 'lucide-react';
import { ChessPost } from '@/types';
import { PostCard } from '@/components/PostCard';

export const Community: React.FC = () => {
  const { user: _user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [newPost, setNewPost] = useState('');
  const [page, setPage] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: postsData, isLoading, error } = useQuery(
    ['posts', page],
    () => apiClient.getPosts(page, 10),
    {
      keepPreviousData: true,
    }
  );

  // Check if current user has a Chess.com profile - moved to top level
  const { data: userProfile } = useQuery(
    'userProfile',
    () => apiClient.getUserProfile(),
    {
      retry: false,
      onError: () => {
        // User doesn't have a profile yet
      }
    }
  );

  const createPostMutation = useMutation(
    (content: string) => apiClient.createPost(content),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('posts');
        setNewPost('');
        setIsSubmitting(false);
      },
      onError: () => {
        setIsSubmitting(false);
      }
    }
  );

  const handleSubmitPost = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPost.trim() || newPost.length > 1000) return;

    setIsSubmitting(true);
    createPostMutation.mutate(newPost.trim());
  };

  const posts = postsData?.data?.posts || [];
  const pagination = postsData?.data?.pagination;
  const currentUserHasChessProfile = userProfile?.data?.chessUsername;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Community</h1>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-start space-x-4">
                <div className="bg-gray-200 h-10 w-10 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="bg-gray-200 h-4 w-32 rounded"></div>
                  <div className="bg-gray-200 h-3 w-full rounded"></div>
                  <div className="bg-gray-200 h-3 w-24 rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Error loading posts</h2>
        <p className="text-gray-600 mb-4">Failed to load community posts.</p>
        <Button onClick={() => window.location.reload()}>Try Again</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Community</h1>
        <p className="text-gray-600 mt-1">Share your thoughts and connect with fellow chess players</p>
      </div>

      {/* Create Post */}
      <Card>
        <CardHeader>
          <CardTitle>Share Something</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmitPost} className="space-y-4">
            <div>
              <textarea
                value={newPost}
                onChange={(e) => setNewPost(e.target.value)}
                placeholder="Share your thoughts, game highlights, or chess tips..."
                className="w-full h-24 p-3 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                maxLength={1000}
              />
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-gray-500">
                  {newPost.length}/1000 characters
                </span>
                <Button
                  type="submit"
                  loading={isSubmitting}
                  disabled={!newPost.trim() || newPost.length > 1000 || isSubmitting}
                  size="sm"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Post
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Posts */}
      <div className="space-y-4">
        {posts.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No posts yet</h3>
              <p className="text-gray-600">Be the first to share something with the community!</p>
            </CardContent>
          </Card>
        ) : (
          posts.map((post: ChessPost) => (
            <PostCard key={post._id} post={post} />
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing page {pagination.page} of {pagination.totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  {pagination.page} / {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Connect Chess.com Profile Section */}
      {!currentUserHasChessProfile && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <span>Connect Your Chess.com Profile</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-orange-800">
                You haven't connected your Chess.com profile yet. Connect it to join the leaderboard and compete with your colleagues!
              </p>
              <div className="flex space-x-3">
                <Button
                  onClick={() => navigate('/chess-setup')}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Connect Chess.com Profile
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/profile')}
                >
                  View Profile
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}; 